import React, { useState, useCallback, useEffect, useRef } from "react";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";

import { IoIosSearch } from "react-icons/io";
import { SearchBarContainer } from "./SearchBarStyled";
import { TContentType } from "../../../../types/helpers";
import BuscasService from "../../../../services/buscas.service";

export interface Props {
    helpText?: string;
    contentType: TContentType;
    onSearchResults?: (results: any) => void;
    onLoading?: (loading: boolean) => void;
    showFilters?: boolean;
}

// All available content types
export const ALL_SEARCH_TYPES = ['ARTIGO', 'CURSO', 'AULA', 'VIDEO', 'TOPICO'] as const;

const TIPOS_BUSCA = [
    { value: "", label: "Todos os tipos" },
    { value: "ARTIGO", label: "Artigo" },
    { value: "CURSO", label: "Curso" },
    { value: "AULA", label: "Aula" },
    { value: "VIDEO", label: "Vídeo" },
    { value: "TOPICO", label: "Notícia" },
];

const ORDENACAO_OPTIONS = [
    { value: "", label: "Relevância" },
    { value: "titulo", label: "Título (A-Z)" },
    { value: "-titulo", label: "Título (Z-A)" },
    { value: "tipo", label: "Tipo (A-Z)" },
    { value: "-tipo", label: "Tipo (Z-A)" },
];

const SearchBarWrapper: React.FC<Props> = ({ helpText, onSearchResults, onLoading, showFilters = false }: Props) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedTipos, setSelectedTipos] = useState<string[]>([]);
    const [ordering, setOrdering] = useState("");
    const [loading, setLoading] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);
    const buscasServiceRef = useRef<BuscasService>(new BuscasService());

    const handleSearch = useCallback(
        async (query: string = searchQuery) => {
            if (!query.trim() && selectedTipos.length === 0) return;

            // Cancel previous request if it exists
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            // Create new abort controller for this request
            abortControllerRef.current = new AbortController();

            setLoading(true);
            onLoading?.(true);

            try {
                const results = await buscasServiceRef.current.searchComplete({
                    q: query,
                    tipos: selectedTipos,
                    ordering,
                    page: 1,
                });

                // Only update state if request wasn't cancelled
                if (!abortControllerRef.current?.signal.aborted) {
                    onSearchResults?.(results);
                }
            } catch (error) {
                // Only handle error if request wasn't cancelled
                if (!abortControllerRef.current?.signal.aborted) {
                    console.error("Erro na busca:", error);
                    onSearchResults?.({ error: true, message: "Erro ao realizar busca" });
                }
            } finally {
                if (!abortControllerRef.current?.signal.aborted) {
                    setLoading(false);
                    onLoading?.(false);
                }
            }
        },
        [searchQuery, selectedTipos, ordering, onSearchResults, onLoading]
    );

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        handleSearch();
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleSearch();
        }
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    return (
        <SearchBarContainer>
            <form onSubmit={handleSubmit} noValidate autoComplete="off">
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                    <Box sx={{ flexGrow: 1, minWidth: "250px" }}>
                        <TextField
                            fullWidth
                            id="search"
                            variant="outlined"
                            color="primary"
                            placeholder="O que você procura"
                            helperText={helpText}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={handleKeyPress}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IoIosSearch size="24" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    {showFilters && (
                        <>
                            <FormControl size="small" sx={{ minWidth: 140 }}>
                                <InputLabel>Tipo</InputLabel>
                                <Select multiple value={selectedTipos} onChange={(e) => setSelectedTipos(e.target.value as string[])} label="Tipo">
                                    {TIPOS_BUSCA.slice(1).map((tipo) => (
                                        <MenuItem key={tipo.value} value={tipo.value}>
                                            {tipo.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl size="small" sx={{ minWidth: 140 }}>
                                <InputLabel>Ordenar</InputLabel>
                                <Select value={ordering} onChange={(e) => setOrdering(e.target.value)} label="Ordenar">
                                    {ORDENACAO_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </>
                    )}

                    <Button type="submit" variant="contained" disabled={loading} sx={{ height: "40px", minWidth: "100px" }}>
                        {loading ? <CircularProgress size={20} /> : "Buscar"}
                    </Button>
                </Box>
            </form>
        </SearchBarContainer>
    );
};

export { SearchBarWrapper as SearchBar };
